import { Controller } from "@hotwired/stimulus"
import { ditherTile } from "../vendor/dither_tile"

const PARTS = ["pin", "plate", "rete", "rule", "horse"]
const WINDOWS = {
  pin: [0.08, 0.28, 0.72, 0.90],
  plate: [0.10, 0.24, 0.72, 0.86],
  rete: [0.15, 0.29, 0.68, 0.80],
  rule: [0.20, 0.34, 0.64, 0.76],
  horse: [0.25, 0.38, 0.60, 0.72]
}
const clamp = (value) => Math.max(0, Math.min(1, value))
const segment = (progress, start, end) => clamp((progress - start) / (end - start))
const ease = (value) => value < 0.5 ? 4 * value ** 3 : 1 - (-2 * value + 2) ** 3 / 2
const amount = (progress, [enter, seated, leave, apart]) =>
  clamp(ease(segment(progress, enter, seated)) - ease(segment(progress, leave, apart)))

let nextId = 0

// FIG. 01 screens its five moving parts; the stationary mater and all labels remain clear.
export default class extends Controller {
  connect() {
    if (this.element.localName !== "astrolabe-assembly" || !this.element.shadowRoot) return
    this.frameId = null
    this.started = false
    this.inView = false
    this.figure = this.element
    this.motion = matchMedia("(prefers-reduced-motion: reduce)")
    this.onMotion = () => this.sync()
    this.onVisibility = () => this.sync()
    this.motion.addEventListener?.("change", this.onMotion)
    document.addEventListener("visibilitychange", this.onVisibility)
    this.installFilters()

    if (typeof IntersectionObserver === "function") {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting
        this.sync()
      }, { threshold: 0.1 })
      this.observer.observe(this.figure)
    } else {
      this.inView = true
      this.sync()
    }
  }

  disconnect() {
    this.observer?.disconnect()
    this.motion?.removeEventListener?.("change", this.onMotion)
    document.removeEventListener("visibilitychange", this.onVisibility)
    cancelAnimationFrame(this.frameId)
    this.frameId = null
    this.figure?.pause()
    PARTS.forEach((part) => this.figure?.classList.remove(`d-${part}`))
    this.style?.remove()
    this.defs?.remove()
  }

  installFilters() {
    const prefix = `astrolabe-assembly-dither-${++nextId}`
    const root = this.figure.shadowRoot
    this.defs = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    this.defs.setAttribute("aria-hidden", "true")
    this.defs.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none"
    this.defs.innerHTML = PARTS.map((part) => `<filter id="${prefix}-${part}" filterUnits="userSpaceOnUse" x="-40" y="-40" width="560" height="440" color-interpolation-filters="sRGB">
      <feImage class="tile" x="0" y="0" width="8" height="8" preserveAspectRatio="none" result="tile"/>
      <feTile in="tile" result="screen"/><feColorMatrix in="screen" type="luminanceToAlpha" result="threshold"/>
      <feGaussianBlur class="blur" in="SourceAlpha" stdDeviation="0" result="spread"/>
      <feComponentTransfer in="spread" result="cover"><feFuncA class="gain" type="linear" slope="1" intercept="0"/></feComponentTransfer>
      <feComposite in="cover" in2="threshold" operator="arithmetic" k1="0" k2="1" k3="-1" k4="0" result="diff"/>
      <feComponentTransfer in="diff" result="bits"><feFuncA type="linear" slope="60" intercept="0"/></feComponentTransfer>
      <feComposite in="SourceGraphic" in2="bits" operator="in"/>
    </filter>`).join("")
    this.style = document.createElement("style")
    this.style.textContent = PARTS.map((part) =>
      `:host(.d-${part}) g.pt-${part}{filter:url(#${prefix}-${part})}`
    ).join("")
    root.append(this.style, this.defs)

    this.filters = Object.fromEntries(PARTS.map((part) => {
      const filter = this.defs.querySelector(`#${prefix}-${part}`)
      const tile = filter.querySelector(".tile")
      tile.setAttribute("href", ditherTile())
      return [part, { tile, blur: filter.querySelector(".blur"), gain: filter.querySelector(".gain") }]
    }))
  }

  sync() {
    if (!this.figure) return
    if (this.motion.matches) {
      cancelAnimationFrame(this.frameId)
      this.frameId = null
      this.figure.pause()
      this.figure.setAttribute("time", String(this.figure.duration / 2))
      PARTS.forEach((part) => this.figure.classList.remove(`d-${part}`))
      return
    }

    this.figure.removeAttribute("time")
    if (!this.inView || document.hidden) {
      cancelAnimationFrame(this.frameId)
      this.frameId = null
      this.figure.pause()
      return
    }
    if (!this.started) {
      this.figure.currentTime = 0
      this.started = true
    }
    this.figure.play()
    if (!this.frameId) this.frameId = requestAnimationFrame(() => this.frame())
  }

  frame() {
    this.frameId = null
    const progress = this.figure.currentTime / this.figure.duration
    PARTS.forEach((part) => {
      const fidelity = 0.19 + 0.81 * amount(progress, WINDOWS[part])
      const screened = fidelity < 0.985
      this.figure.classList.toggle(`d-${part}`, screened)
      if (!screened) return
      const filter = this.filters[part]
      const side = ((1.9 + (1.3 - 1.9) * fidelity ** 0.85) * 4).toFixed(2)
      filter.tile.setAttribute("width", side)
      filter.tile.setAttribute("height", side)
      filter.blur.setAttribute("stdDeviation", (1.95 * (1 - fidelity) ** 1.3).toFixed(2))
      filter.gain.setAttribute("slope", (1.35 + 11 * fidelity ** 1.7).toFixed(2))
    })
    if (this.inView && !document.hidden && !this.motion.matches)
      this.frameId = requestAnimationFrame(() => this.frame())
  }
}
